import { Box, Button, Link, Modal, Text, ThemeOverride } from "@modernatx/ui-kit-react";
import { usePathname } from "next/navigation";
import React from "react";

import {
  PRESCRIBING_INFO_KEYS,
  PRESCRIBING_INFO_LINKS
} from "@/content-V2/profiles/_common/prescribing-info-modal";
import { useExperience } from "@/context/ExperienceContext";

const PRODUCT_TYPE_SPIKEVAX = "spikevax";
const PRODUCT_TYPE_MNEXSPIKE = "mnexspike";
const PRODUCT_TYPE_EUA = "eua";

export const PrescribingInfoModal: React.FC = () => {
  const pathname = usePathname();
  const { host } = useExperience();
  const [modalVariant, setModalVariant] = React.useState<
    | typeof PRODUCT_TYPE_SPIKEVAX
    | typeof PRODUCT_TYPE_MNEXSPIKE
    | typeof PRODUCT_TYPE_EUA
    | undefined
  >(undefined);
  const [isOpen, setIsOpen] = React.useState(false);

  const CONSUMER_MODAL_INTRO_TEXT = "There has been a recent update to the ";
  const EUA_MODAL_INTRO_TEXT =
    "There has been a recent update to the Moderna COVID-19 Vaccine (2024-2025 Formula) ";
  const MODAL_AND_TEXT = " and ";
  const CONSUMER_MODAL_TRAILING_TEXT =
    " for COVID-19 mRNA vaccines. Please refer to section 5.2 Myocarditis and Pericarditis and section 6.2 Postmarketing Experience for more information.";
  const EUA_MODAL_TRAILING_TEXT =
    ". Please refer to section 5.2 Myocarditis and Pericarditis and section 6.2 Postmarketing Experience for more information.";

  React.useEffect(() => {
    if (typeof window === "undefined" || host !== "products.modernatx.com" || !pathname) {
      return;
    }
    if (/^\/(us\/)?spikevax(pro)?\/6-months-to-11-years/.test(pathname)) {
      if (!sessionStorage.getItem(PRESCRIBING_INFO_KEYS.eua)) {
        setModalVariant(PRODUCT_TYPE_EUA);
        setIsOpen(true);
      }
    } else if (/^\/(us\/)?spikevax/.test(pathname) || /^\/(us\/)?spikevaxpro/.test(pathname)) {
      if (!sessionStorage.getItem(PRESCRIBING_INFO_KEYS.spikevax)) {
        setModalVariant(PRODUCT_TYPE_SPIKEVAX);
        setIsOpen(true);
      }
    } else if (/^\/mnexspike/.test(pathname) || /^\/mnexspikepro/.test(pathname)) {
      if (!sessionStorage.getItem(PRESCRIBING_INFO_KEYS.mnexspike)) {
        setModalVariant(PRODUCT_TYPE_MNEXSPIKE);
        setIsOpen(true);
      }
    }
  }, [pathname, host]);

  const handleClose = React.useCallback(() => {
    if (modalVariant) {
      sessionStorage.setItem(PRESCRIBING_INFO_KEYS[modalVariant], "1");
    }
    setIsOpen(false);
    setModalVariant(undefined);
  }, [modalVariant]);

  const renderContent = () => {
    switch (modalVariant) {
      case PRODUCT_TYPE_SPIKEVAX:
      case PRODUCT_TYPE_MNEXSPIKE:
        return (
          <>
            <Text as="h2" size="lg" variant="medium" sx={{ mb: 4 }}>
              Updated Prescribing Information
            </Text>
            <Text as="p" variant="regular" sx={{ mb: 8 }}>
              {CONSUMER_MODAL_INTRO_TEXT}
              <Link href={PRESCRIBING_INFO_LINKS[modalVariant].pi} target="_blank">
                Prescribing Information (PI)
              </Link>
              {MODAL_AND_TEXT}
              <Link href={PRESCRIBING_INFO_LINKS[modalVariant].info} target="_blank">
                Patient Prescribing Information (PPI)
              </Link>
              {CONSUMER_MODAL_TRAILING_TEXT}
            </Text>
          </>
        );
      case PRODUCT_TYPE_EUA:
        return (
          <>
            <Text as="h2" size="lg" variant="medium" sx={{ mb: 4 }}>
              Updated Prescribing Information
            </Text>
            <Text as="p" variant="regular" sx={{ mb: 8 }}>
              {EUA_MODAL_INTRO_TEXT}
              <Link href={PRESCRIBING_INFO_LINKS[modalVariant].providers} target="_blank">
                Provider
              </Link>
              {MODAL_AND_TEXT}
              <Link href={PRESCRIBING_INFO_LINKS[modalVariant].recipients} target="_blank">
                Recipient Fact Sheets
              </Link>
              {EUA_MODAL_TRAILING_TEXT}
            </Text>
          </>
        );
      default:
        return null;
    }
  };

  return (
    <>
      <ThemeOverride
        theme={{
          colorsLight: {
            backgroundOverlay01: "rgba(37, 38, 39, 0.4)"
          }
        }}
      >
        <Modal isOpen={isOpen} size="sm" aria-label="update-info-modal" onDismiss={handleClose}>
          <Box sx={{ p: [5, 6] }}>
            {renderContent()}
            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <Button size="small" onClick={handleClose}>
                Okay
              </Button>
            </Box>
          </Box>
        </Modal>
      </ThemeOverride>
    </>
  );
};
